
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { Formatters, Messages } from '../../../../../gen-src/Localize';
import { DatabaseTableOrderField } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createIconCellFormatter,
  createLabelColumnFormatter,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  ExplorerStyledDisplayGrid,
  FeatureFlagsContext,
  GraphLabel,
  GraphQLError,
  GridSortProps,
  LabelContent,
  reportGraphQLErrors,
  SiteUrlsContext,
  TableMediumIcon,
  TypedColumn,
  UnsortableColumnKey,
  UseRenderLabelsParams,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { getLabelParamsForGrids, labelCategoryToString, LabelGridWrapperProps } from '../Labels/LabelsHookHelper';
import {
  getDatabaseDisplayString,
  tableDatabaseCellTitle,
  tableDatabaseGroupIcon,
} from '../datagrid/cellFormatters/DatabaseNameCell';
import {
  createObfuscatedLinkCellFormatter,
  createObfuscatedLocationLinkOrTextCellFormatter,
  createObfuscatedNamesWithCountCellFormatter,
  createObfuscatedTextWithIconCellFormatter,
} from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { createProjectLocationLinkCellFormatter } from '../datagrid/cellFormatters/ProjectLocationCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import {
  GlobalAssetGridDC3ColumnFactoryParams,
} from '../sorting/makeColumnFactoryParams';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';
import { TablesDisplayGridRowData } from '../table/types/TablesDisplayGridRowData';
import { LocationTypes } from './types/LocationTypes';
import { CellFormatter } from './types/SharedDisplayGridTypes';

const shouldBeObfuscated: (rowData: TablesDisplayGridRowData) => boolean = ({
  name,
}) => name === null;

type TablesColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<TablesValidSortFields>,
  TablesDisplayGridRowData,
  TablesValidSortFields
>;

function iconColumn(): JSX.Element {
  return <TableMediumIcon isCertified={false} />;
}

export function columns(siteUrls: SiteUrls): ReadonlyArray<TablesColumnFactory> {
  const columns: ReadonlyArray<TablesColumnFactory> = [
    (sortParams) => ({
      name: 'type',
      dataKey: DatabaseTableOrderField.IS_CERTIFIED,
      headerText: Messages.GridHeaderType(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createIconCellFormatter(
        () => iconColumn(),
        (rowData) =>
          rowData.__typename === 'DatabaseTable' && rowData.isCertified
            ? ''
            : Messages.Table()
      ),
      resizable: false,
      sortable: true,
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'CERTIFICATION'),
    () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
    (params) => ({
      name: 'name',
      dataKey: DatabaseTableOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(params),
      cellFormatter: createObfuscatedLinkCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name,
        (urls, { id }) => urls.catalogTablePath(id),
        undefined, // noPadding
        true // disableTabIndex
      ),
      growToFit: true,
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'virtualConnections',
      dataKey: DatabaseTableOrderField.DOWNSTREAM_VIRTUAL_CONNECTIONS_COUNT,
      headerText: Messages.GridHeaderVirtualConnection(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamVirtualConnectionsConnection }) =>
          downstreamVirtualConnectionsConnection
            ? Formatters.formatCount(
              downstreamVirtualConnectionsConnection.totalCount
            )
            : null
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'workbooks',
      dataKey: DatabaseTableOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
      headerText: Messages.GridHeaderWorkbooks(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamWorkbooksConnection }) =>
          downstreamWorkbooksConnection
            ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
            : null
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'datasources',
      dataKey: DatabaseTableOrderField.DOWNSTREAM_DATASOURCES_COUNT,
      headerText: Messages.GridHeaderDatasources(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamDatasourcesConnection }) =>
          downstreamDatasourcesConnection
            ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
            : null
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'database',
      dataKey: '',
      headerText: Messages.GridHeaderDatabaseNamePath(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedTextWithIconCellFormatter<
        TablesDisplayGridRowData
      >(
        shouldBeObfuscated,
        getDatabaseDisplayString,
        tableDatabaseGroupIcon,
        tableDatabaseCellTitle,
        undefined, // placeholder
        (rowData) => rowData.id === ''
      ),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: 'columns',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderColumns(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedNamesWithCountCellFormatter(
        shouldBeObfuscated,
        ({ columnsConnection }) =>
          columnsConnection
            ? columnsConnection.nodes.map((column) => column.name || '')
            : [],
        ({ columnsConnection }) =>
          columnsConnection ? columnsConnection.totalCount : 0,
        undefined, // maxShown
        undefined, // ObfuscatedComponent
        (rowData) => rowData.id === ''
      ),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: 'projectName',
      dataKey: DatabaseTableOrderField.PROJECT_NAME,
      headerText: Messages.GridHeaderLocation(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLocationLinkOrTextCellFormatter(
        shouldBeObfuscated,
        (rowData) => getProjectName(rowData.__typename, rowData.projectName, rowData.isEmbedded),
        (urls, rowData) => getProjectLink(urls, rowData.projectVizportalUrlId),
        false,
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'contact',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderContact(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createUserNameLinkCellFormatter(
        (rowData) =>
          rowData.__typename === 'DatabaseTable' ? rowData.contact : null,
        true,
        (rowData) => rowData.id === ''
      ),
      resizable: true,
      sortable: false,
    }),
  ];

  return columns.filter(Boolean) as ReadonlyArray<TablesColumnFactory>;
}

/**
 * @deprecated These cell formatters are already in use in the columns above. These defs below will be removed once we have
 * the columns used properly in vpc.
 */
const cellFormattersForCrass: Record<string, CellFormatter> = {
  typeForCrass: {
    type: 'table',
    cellFormatter: createIconCellFormatter(
      () => iconColumn(),
      (rowData) =>
        rowData.__typename === 'DatabaseTable' && rowData.isCertified
          ? ''
          : Messages.Table()
    ),
  },
  name: {
    type: 'table',
    cellFormatter: createObfuscatedLinkCellFormatter(
      shouldBeObfuscated,
      ({ name }) => name,
      (urls, { id }) => urls.catalogTablePath(id),
      undefined, // noPadding
      true, // disableTabIndex
      undefined, // ObfuscatedComponent
      (rowData) => rowData.id === ''
    ),
  },
  downstreamWorkbookCount: {
    type: 'table',
    cellFormatter: createTextCellFormatter(
      ({ downstreamWorkbooksConnection }) =>
        downstreamWorkbooksConnection
          ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
          : null
    ),
  },
  downstreamDataSourcesCount: {
    type: 'table',
    cellFormatter: createTextCellFormatter(
      ({ downstreamDatasourcesConnection }) =>
        downstreamDatasourcesConnection
          ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
          : null
    ),
  },
  database: {
    type: 'table',
    cellFormatter: createObfuscatedTextWithIconCellFormatter<
      TablesDisplayGridRowData
    >(
      shouldBeObfuscated,
      getDatabaseDisplayString,
      tableDatabaseGroupIcon,
      tableDatabaseCellTitle,
      undefined, // placeholder
      (rowData) => rowData.id === ''
    ),
  },
  columns: {
    type: 'table',
    cellFormatter: createObfuscatedNamesWithCountCellFormatter(
      shouldBeObfuscated,
      ({ columnsConnection }) =>
        columnsConnection
          ? columnsConnection.nodes.map((column) => column.name || '')
          : [],
      ({ columnsConnection }) =>
        columnsConnection ? columnsConnection.totalCount : 0,
      undefined, // maxShown
      undefined, // ObfuscatedComponent
      (rowData) => rowData.id === ''
    ),
  },
  contact: {
    type: 'table',
    cellFormatter: createUserNameLinkCellFormatter(
      (rowData) =>
        rowData.__typename === 'DatabaseTable' ? rowData.contact : null,
      true,
      (rowData) => rowData.id === ''
    ),
  },
  tableProjectLocation: {
    type: 'table',
    cellFormatter: createProjectLocationLinkCellFormatter(
      (rowData) => { return rowData.containerId === undefined ? null : rowData.containerId; },
      (rowData) => { return rowData.containerName === undefined ? null : rowData.containerName; },
      true,
      (rowData) => { return rowData.isEmbedded === undefined ? null : rowData.isEmbedded; },
      (rowData) => rowData.id === ''
    ),
  },
};


export const tablesWithCrassCellFormatters = cellFormattersForCrass;

export type TablesDisplayGridProps = UsualDisplayGridProps<
  TablesDisplayGridRowData,
  GraphQLError
> &
  GridSortProps<TablesValidSortFields>;

export function TablesDisplayGrid(props: TablesDisplayGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);
  const siteUrls = React.useContext(SiteUrlsContext);
  const cols = columns(siteUrls)
    .map((columnFactory) => columnFactory(props))
    .filter(column => {
      if (column.name === 'virtualConnections' && !publishedConnectionsLineageEnabled) {
        return false;
      }
      return true;
    });

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
function getProjectName(__typename: string, projectName: string | null | undefined, isEmbedded: boolean | null): string | null | LocationTypes {
  if (__typename === 'DatabaseTable') {
    if (projectName !== null && projectName !== undefined) {
      if (isEmbedded) {
        return LocationTypes.NotApplicable;
      } else if (projectName.length === 0) {
        return LocationTypes.None;
      } else {
        return projectName;
      }
    } else {
      return LocationTypes.None;
    }
  } else {
    return null;
  }
}

function getProjectLink(urls: SiteUrls, projectVizportalUrlId: string | null | undefined): string | null {
  if (projectVizportalUrlId !== undefined && projectVizportalUrlId !== null && projectVizportalUrlId.length !== 0) {
    return urls.projectPath(projectVizportalUrlId ? projectVizportalUrlId : '');
  } else {
    return null;
  }
}

function getChildWarningsFromRowDataImpl({ columnsConnectionWithWarnings }: TablesDisplayGridRowData): ReadonlyArray<GraphLabel> {
  let result: GraphLabel[] = [];
  if (!columnsConnectionWithWarnings) {
    return result;
  }

  columnsConnectionWithWarnings.nodes.forEach(n => {
    result = result.concat(n.labels);
  });

  return result;
}

function getLabelContentFromRowData(rowData: TablesDisplayGridRowData, urls: SiteUrls) {
  return {
    contentId: rowData.id,
    contentLuid: rowData.__typename === 'DatabaseTable' ? rowData.luid : '',
    contentType: rowData.contentType,
    contentName: rowData.name ?? undefined,
    contentUrl: urls.catalogTableUrl(rowData.id),
    subjectArea: rowData.__typename === 'DatabaseTable' ? rowData.__typename : undefined
  };
}


function createLabelGridColumnFormatter(urls: SiteUrls, category: LabelGridWrapperProps['category']):
  TypedColumn<TablesDisplayGridRowData, TablesValidSortFields> {

  function getPropsFromRowData(rowData: TablesDisplayGridRowData): UseRenderLabelsParams {
    const content: LabelContent = getLabelContentFromRowData(rowData, urls);
    const childWarnings = getChildWarningsFromRowDataImpl(rowData);
    const labels: ReadonlyArray<GraphLabel> = (rowData.__typename !== 'DatabaseTable' || rowData.id === '') ? [] : rowData.labels.concat(childWarnings);
    return getLabelParamsForGrids({ category, labels, urls, source: 'tables', content });
  }

  return createLabelColumnFormatter(labelCategoryToString(category), { overrides: {}, getPropsFromRowData }) as
    TypedColumn<TablesDisplayGridRowData, TablesValidSortFields>;
}
